import React, {useState, useEffect} from 'react';
import { Link } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import { Button, Chip } from '@mui/material';
import Typography from '@mui/material/Typography';

import { adminGetChallenges } from "../../api/Api";
import { Difficulty } from "../../shared/Difficulty";
import { useColors } from '../../theme/ColorModeContext';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';

interface PropsAdminChallenges {
    token: string;
}

interface Challenge {
    id: string;
    name: string;
    app: string;
    category: string;
    type: number;
    difficulty: number;
    points: number;
    active: boolean;
    docker_image: string;
}

const AdminChallenges = (props: PropsAdminChallenges) => {
    const colors = useColors();
    const [challenges, setChallenges] = useState<Challenge[]>([]);
    const token = props.token;

    useEffect(() => {
        const fetchData = async () => {
            const data = await adminGetChallenges(token);
            setChallenges(data.challenges);
        }
        fetchData();
    }, []);

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <CyberpunkTitle
                    title="Challenges"
                    subtitle={`${challenges.length} ENTRIES`}
                    mb="0"
                />
                <Link to="/admin/challenges/add" style={{ textDecoration: 'none' }}>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        sx={{
                            borderColor: colors.accent.cyan,
                            color: colors.accent.cyan,
                            fontFamily: '"Share Tech Mono", monospace',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            padding: '8px 20px',
                            borderRadius: '6px',
                            '&:hover': {
                                backgroundColor: colors.accent.cyanGlow,
                                borderColor: colors.accent.cyan,
                                boxShadow: `0 0 16px ${colors.accent.cyan}44`,
                            },
                        }}
                    >
                        Add Challenge
                    </Button>
                </Link>
            </Box>

            <GlowCard glowOnHover={false}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{
                                borderBottom: `1px solid ${colors.border.accent}`,
                                '& th': {
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    color: colors.accent.cyan,
                                    borderBottom: 'none',
                                    padding: '14px 16px',
                                    backgroundColor: 'transparent',
                                }
                            }}>
                                <TableCell>#</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>App</TableCell>
                                <TableCell>Docker Image</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Difficulty</TableCell>
                                <TableCell align="right">Points</TableCell>
                                <TableCell align="center">Active</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {challenges.map((challenge, idx) => (
                                <TableRow
                                    key={idx}
                                    sx={{
                                        '&:last-child td': { border: 0 },
                                        borderBottom: `1px solid ${colors.border.subtle}`,
                                        transition: 'background-color 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: `rgba(185, 28, 28, 0.04)`,
                                        },
                                        '& td': {
                                            borderBottom: 'none',
                                            padding: '12px 16px',
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Typography sx={{
                                            fontFamily: '"Share Tech Mono", monospace',
                                            fontSize: '0.75rem',
                                            color: colors.text.muted,
                                        }}>
                                            {String(idx + 1).padStart(2, '0')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Link style={{ textDecoration: 'none' }} to={`/admin/challenge/${challenge.id}`}>
                                            <Typography sx={{
                                                fontFamily: '"Orbitron", sans-serif',
                                                fontWeight: 600,
                                                color: colors.text.primary,
                                                '&:hover': { color: colors.accent.cyan },
                                                transition: 'color 0.2s ease',
                                            }}>
                                                {challenge.name}
                                            </Typography>
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{
                                            fontFamily: '"Share Tech Mono", monospace',
                                            fontSize: '0.8rem',
                                            color: colors.text.secondary,
                                        }}>
                                            {challenge.app}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{
                                            fontFamily: '"Share Tech Mono", monospace',
                                            fontSize: '0.75rem',
                                            color: colors.text.secondary,
                                        }}>
                                            {challenge.docker_image || 'all:latest'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={challenge.category}
                                            size="small"
                                            sx={{
                                                fontFamily: '"Share Tech Mono", monospace',
                                                fontSize: '0.65rem',
                                                fontWeight: 600,
                                                letterSpacing: '0.08em',
                                                textTransform: 'uppercase',
                                                backgroundColor: colors.accent.purpleGlow,
                                                color: colors.accent.purple,
                                                border: `1px solid ${colors.accent.purple}44`,
                                                height: '22px',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={challenge.type === 0 ? 'Academy' : 'Mission'}
                                            size="small"
                                            sx={{
                                                fontFamily: '"Share Tech Mono", monospace',
                                                fontSize: '0.65rem',
                                                fontWeight: 600,
                                                letterSpacing: '0.08em',
                                                textTransform: 'uppercase',
                                                backgroundColor: challenge.type === 0 ? colors.accent.cyanGlow : `rgba(255, 145, 0, 0.12)`,
                                                color: challenge.type === 0 ? colors.accent.cyan : colors.accent.orange,
                                                border: `1px solid ${challenge.type === 0 ? colors.accent.cyan : colors.accent.orange}44`,
                                                height: '22px',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Difficulty level={challenge.difficulty} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography sx={{
                                            fontFamily: '"Share Tech Mono", monospace',
                                            fontWeight: 700,
                                            color: colors.accent.cyan,
                                            fontSize: '0.9rem',
                                        }}>
                                            {challenge.points}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        {challenge.active
                                            ? <CheckCircleIcon sx={{ color: colors.accent.green, fontSize: '1.2rem' }} />
                                            : <CancelIcon sx={{ color: colors.text.muted, fontSize: '1.2rem' }} />
                                        }
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {!challenges.length && (
                    <Box sx={{ textAlign: 'center', padding: '60px 0' }}>
                        <CircularProgress size={40} sx={{ color: colors.accent.cyan }} />
                    </Box>
                )}
            </GlowCard>
        </PageContainer>
    );
};

export default AdminChallenges;
